import { MIN_ENROLLMENT_COUNT_FOR_DISPLAY } from "@/components/CourseContent/CourseRunSelector/constants";
import { ProcessedCourse } from "@/common/course/types";
import Icon from "@/components/icons";
import { cn } from "@/lib/utils";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import { getCourseRunStartText } from "@/lib/course/utils/getCourseRunTilePrimaryText";
import useCourseEnrollment from "../../../../ContentRefresh/CourseEnroll/hooks/useCourseEnrollment";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import { isDateInPast } from "@/lib/date";
import CourseEnrollButton from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons";
import MultipleStartDateModal from "@/components/CourseContent/ContentRefresh/MultipleStartDateModal";
import AuditLink from "@/components/CourseContent/ContentRefresh/CourseEnroll/CourseEnrollButtons/AuditLink";
import { CourseRun } from "@/common/courseRun/types";
import { getCourseRunEndText } from "@/lib/course/utils/getCourseRunTitleSecondaryText";
import getNextCourseOneLineText from "../../../../ContentRefresh/CourseEnroll/utils/getNextCourseOneLineText";
import useTranslations from "@/i18n/useTranslations";

export interface Props {
  course: ProcessedCourse;
  courseRun: CourseRun;
  name?: string;
  location?: string;
  buttonClass?: string;
  className?: string;
}

export default function MidPageCTA({
  course,
  courseRun,
  name,
  location,
  buttonClass,
}: Props) {
  const t = useTranslations("Course");
  const translate =
    t as unknown as import("@/lib/course/utils/getCourseInformation").CourseInfoTranslate;
  const { isUserEnrolledInRun, isCourseEnrollable, isCourseArchived } =
    useCourseEnrollment({
      courseRun,
      course,
    });
  const { showHeaderPrice, showHeaderPriceMobile } =
    useSingleCourseTrafficConfig({ course });

  const courseRunPrice = getCoursePrice(courseRun);
  const enrollmentCount = course.enrollmentCount;

  const showEnrollmentCount =
    !isCourseArchived &&
    courseRun.isEnrollable &&
    enrollmentCount &&
    enrollmentCount >= MIN_ENROLLMENT_COUNT_FOR_DISPLAY;

  const enrollmentEnded = courseRun.enrollmentEnd
    ? isDateInPast(courseRun.enrollmentEnd)
    : false;

  const endText = getCourseRunEndText(courseRun, translate);

  const multipleRunsAvailable = (course.activeCourseRuns || []).length > 1;

  let seeMoreDatesHandler = false;
  if (multipleRunsAvailable) {
    seeMoreDatesHandler = true;
  }

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-4 items-center justify-between w-auto md:py-0 md:px-0 md:rounded-none md:border-none py-6 px-4 rounded-xl border-putty-400 border",
        )}
      >
        {isCourseArchived || !isCourseEnrollable ? (
          <span className="font-bold text-base">
            {t("thisCourseIsArchived")}
          </span>
        ) : (
          <div className="text-center">
            <h2 className="md:text-3xl font-bold my-0">
              {getNextCourseOneLineText(
                getCourseRunStartText(
                  courseRun,
                  !!seeMoreDatesHandler,
                  translate,
                ),
                translate,
              )}
            </h2>
            {endText && (
              <div className={seeMoreDatesHandler ? "text-sm" : ""}>
                {endText}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col-reverse md:flex-row gap-4">
          {showEnrollmentCount ? (
            <div
              className={`items-center text-base ${showHeaderPriceMobile ? "hidden" : "flex"} ${showHeaderPrice ? "md:hidden" : "md:flex"}`}
            >
              <span className="font-bold m-0">
                {enrollmentCount.toLocaleString()}
              </span>
              <span className="m-0">&nbsp;{t("learnersEnrolled")}</span>
            </div>
          ) : null}

          {isCourseEnrollable ? (
            <div className="flex items-center">
              <CourseEnrollButton
                buttonClass={buttonClass}
                courseRun={courseRun}
                course={course}
                name={name}
                location={location}
                price={courseRunPrice}
              />
            </div>
          ) : !isUserEnrolledInRun && enrollmentEnded ? (
            <span>{t("futureDatesToBeAnnounced")}</span>
          ) : null}
        </div>

        <div className="flex flex-row gap-4">
          {seeMoreDatesHandler ? (
            <MultipleStartDateModal
              course={course}
              showEnrollmentCount={showEnrollmentCount}
            >
              <button
                type="button"
                className="flex flex-row gap-1 justify-end items-center"
              >
                <Icon type="calendar-checked" />
                <span className="underline underline-offset-2">
                  {t("seeMoreDates")}
                </span>
              </button>
            </MultipleStartDateModal>
          ) : null}
          {isCourseEnrollable ? (
            <AuditLink
              courseRun={courseRun}
              course={course}
              variant="icon"
              name={name}
              location={location}
              className="hidden md:inline-flex"
            />
          ) : null}
        </div>
      </div>
      {isCourseEnrollable ? (
        <AuditLink
          courseRun={courseRun}
          course={course}
          variant="icon"
          name={name}
          location={location}
          className="inline-flex md:hidden mt-4 self-end w-full"
        />
      ) : null}
    </>
  );
}
